import { DOCUMENT } from '@angular/common';
import { Injectable, inject } from '@angular/core';
import { ActivatedRouteSnapshot } from '@angular/router';
import Config from 'config/config';

import {
  buildVendorPortalDestinationUrl,
  isSafeVendorsBaseUrl,
  legacyWizardPortalPath
} from '../../vendor-redirect';

/**
 * The legacy vendor registration/renewal wizard is gone. Anything still arriving
 * at one of its URLs goes to vive-vendors, **unconditionally**.
 *
 * `VendorPortalRedirectGuard` deliberately passes traffic through unless the
 * session is a vendor's. That is correct for the routes it protects — it must
 * not bounce a client or staff login — but it is exactly the hole that kept the
 * wizard reachable: a vendor tapping an emailed link arrives signed out, so that
 * guard let them through and they completed registration in the retired funnel.
 *
 * This guard is attached ONLY to the `vendor-registration/**` routes, so it can be
 * absolute where the shared one cannot. It never returns true: those URLs have no
 * destination in this app any more. Signed in or not, the target is the same URL;
 * vive-vendors reads the shared session cookie.
 *
 * Old links stay working. The route paths are kept (emails already in inboxes, and
 * anything bookmarked) and translated to their portal equivalent rather than
 * deleted into the 404 wildcard.
 */
@Injectable({
  providedIn: 'root'
})
export class LegacyVendorWizardRedirectGuard {
  // The cross-origin hop has to go through `location.replace`, so it is taken via
  // the DOCUMENT token rather than the global: `window.location.replace` is
  // non-writable in current Chrome and cannot be spied on directly.
  private readonly document = inject(DOCUMENT);

  public canActivate(route: ActivatedRouteSnapshot): boolean {
    const { vendorsAppUrl } = Config.getEnvironmentConfig();

    // No safe target configured — a config regression. Let the route resolve to
    // the 404 rather than navigate somewhere arbitrary.
    if (!vendorsAppUrl || !isSafeVendorsBaseUrl(vendorsAppUrl)) {
      return false;
    }

    // Read the params off the matched route rather than the raw location: these
    // routes carry `:CompanyIds` and `:paymentType`, and `:CompanyIds` may be a
    // comma-separated list, of which the first client is the one to resume.
    const paymentType = route.paramMap.get('paymentType');
    const companyIds = route.paramMap.get('CompanyIds');
    const companyId = companyIds?.split(',')[0]?.trim() ?? null;

    this.document.location.replace(
      buildVendorPortalDestinationUrl(
        vendorsAppUrl,
        legacyWizardPortalPath(paymentType, companyId)
      )
    );

    return false;
  }
}
